'use client';

import type { SoftphoneProvider } from '@poseidon/components/Softphone/softphoneClient.types';
import { ClickToCallButtonPure } from '@poseidon/components/Softphone/ClickToCallButtonPure';

import { useSoftphoneLauncher } from '~/components/SoftphoneProvider';

export interface ClickToCallButtonProps {
  teamId: string;
  /** Public contact id (contact_public_id). */
  contactId: string;
  contactName?: string;
  phoneNumber?: string | null;
  /** Contact photo for the dialer avatar; initials fallback when absent. */
  imageSrc?: string | null;
  /** Team feature flag — when false the affordance is hidden entirely. */
  enabled: boolean;
  /**
   * Whether the rep has a caller number provisioned. When false (and `enabled` is true), the
   * button renders greyed/disabled with a tooltip instead of disappearing.
   */
  callingConnected: boolean;
  /** The team's carrier; picks the matching softphone client (Telnyx vs Twilio). */
  provider: SoftphoneProvider;
  /**
   * When placing the call for a cadence `call` step, the cadence_step id. Threaded to
   * `startCall` so logging the outcome completes that step server-side.
   */
  cadenceStepId?: string;
  /** Called after a call outcome is logged (e.g. to invalidate the contact / reminder caches). */
  onCallLogged?: () => void;
  className?: string;
}

/**
 * Dashboard click-to-call trigger. A thin button that hands the contact off to the app-global
 * `SoftphoneProvider` (mounted in `_app.tsx`), which owns the actual call + dialer so it survives
 * navigation. The carrier is chosen server-side and passed in as `provider`, so this button is the
 * same for Telnyx and Twilio — only the client the provider builds differs.
 */
export function ClickToCallButton({
  teamId,
  contactId,
  contactName,
  phoneNumber,
  imageSrc,
  enabled,
  callingConnected,
  provider,
  cadenceStepId,
  onCallLogged,
  className,
}: ClickToCallButtonProps) {
  const { startCall, isBusy } = useSoftphoneLauncher();

  return (
    <ClickToCallButtonPure
      enabled={enabled}
      callingConnected={callingConnected}
      phoneNumber={phoneNumber}
      busy={isBusy}
      onCall={() =>
        startCall({
          teamId,
          contactId,
          contactName,
          phoneNumber: phoneNumber ?? '',
          imageSrc,
          provider,
          cadenceStepId,
          onCallLogged,
        })
      }
      className={className}
    />
  );
}
